import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../api/registryApi';

export interface SourceQuery<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Loads once per `load` identity (wrap it in useCallback) and on reload; ignores stale results. */
export function useSourceQuery<T>(load: () => Promise<T>): SourceQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setLoading(false);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setData(null);
        setError(errorMessage(loadError));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, version]);

  const reload = useCallback(() => setVersion((n) => n + 1), []);
  return { data, loading, error, reload };
}
